import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import AddMetricModal from "@/Profile/components/OverViewsPage/AddMetricModal/AddMetricModal";
import { addVitalSignal, fetchVitalSignals } from "@/Profile/redux/thunks/vitalThunks";
import "./VitalsAndExamSection.css";

const examinationFields = [
  { key: "glasgowScore", type: "number", min: 3, max: 15 },
];

const edemaOptions = ["none", "mild", "moderate", "severe"];

const measurementFields = [
  { key: "pulse", type: "HEART_RATE", unitKey: "unitBpm" },
  { key: "oxygenSaturation", type: "OXYGEN_SAT", unitKey: "unitPercent" },
  { key: "temperature", type: "TEMPERATURE", unitKey: "unitCelsius" },
  { key: "respiratoryRate", type: "RESP_RATE", unitKey: "unitPerMinute" },
  { key: "bloodGlucose", type: "GLYCEMIA", unitKey: "unitMmolL" },
  { key: "diuresis", type: "DIURESIS", unitKey: "unitMlPerDay" },
];

const bloodPressureLocations = [
  { key: "RIGHT_ARM", labelKey: "rightArm" },
  { key: "LEFT_ARM", labelKey: "leftArm" },
  { key: "STANDING", labelKey: "standing" },
];

const parseBloodPressureLabel = (measurement) => {
  if (normalizeMeasurementType(measurement?.type) !== "OTHER") return null;
  const match = normalizeMeasurementType(measurement?.customLabel).match(
    /^BP_(RIGHT_ARM|LEFT_ARM|STANDING)_(SYS|DIA)$/,
  );
  return match
    ? { location: match[1], type: `BP_${match[2]}` }
    : null;
};

const normalizeMeasurementType = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const isDiuresisMeasurement = (measurement) => {
  const label = normalizeMeasurementType(
    measurement?.customLabel ?? measurement?.label ?? measurement?.name,
  );
  return (
    label.includes("DIURES") ||
    label.includes("DIURESE") ||
    label.includes("URINE_OUTPUT") ||
    label.includes("URINARY_OUTPUT")
  );
};

const measurementDate = (measurement) => {
  const timestamp = new Date(
    measurement?.measuredAt ??
      measurement?.measured_at ??
      measurement?.createdAt ??
      measurement?.date ??
      0,
  ).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const measurementValue = (measurement) =>
  measurement?.value ??
  measurement?.reading ??
  measurement?.measurement ??
  measurement?.result;

const displayMeasurement = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : String(value);
};

const bloodPressureValue = (systolic, diastolic) => {
  const systolicValue = displayMeasurement(measurementValue(systolic));
  const diastolicValue = displayMeasurement(measurementValue(diastolic));
  return systolicValue || diastolicValue
    ? `${systolicValue || "—"}/${diastolicValue || "—"}`
    : "";
};

export default function VitalsAndExamSection({
  examRecord,
  examLoading = false,
  examError,
  userId,
  medicalInfo,
  patientMeasurements,
}) {
  const { t } = useTranslation("neurologue");
  const dispatch = useDispatch();
  const [addMetricOpen, setAddMetricOpen] = useState(false);
  const [metricError, setMetricError] = useState("");
  const [savingMetric, setSavingMetric] = useState(false);
  const [vitalMeasurements, setVitalMeasurements] = useState([]);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [measurementsError, setMeasurementsError] = useState("");
  const [pressureDraft, setPressureDraft] = useState({});
  const [pressureSaving, setPressureSaving] = useState("");
  const [pressureError, setPressureError] = useState("");
  const latestMeasurements = useMemo(() => {
    const latestByType = new Map();
    vitalMeasurements.forEach((measurement) => {
      const bloodPressureLabel = parseBloodPressureLabel(measurement);
      const rawType = normalizeMeasurementType(
        measurement?.type ??
          measurement?.signalType ??
          measurement?.vitalType ??
          measurement?.measurementType ??
          measurement?.name ??
          measurement?.code,
      );
      const type = bloodPressureLabel?.type ||
        (isDiuresisMeasurement(measurement)
          ? "DIURESIS"
          : ({
            SYSTOLIC: "BP_SYS",
            SYSTOLIC_PRESSURE: "BP_SYS",
            BLOOD_PRESSURE_SYS: "BP_SYS",
            DIASTOLIC: "BP_DIA",
            DIASTOLIC_PRESSURE: "BP_DIA",
            BLOOD_PRESSURE_DIA: "BP_DIA",
            PULSE: "HEART_RATE",
            HEARTRATE: "HEART_RATE",
            CARDIAC_RATE: "HEART_RATE",
            SPO2: "OXYGEN_SAT",
            SPO2_PERCENT: "OXYGEN_SAT",
            OXYGENSAT: "OXYGEN_SAT",
            OXYGEN_SATURATION: "OXYGEN_SAT",
            SATURATION_OXYGEN: "OXYGEN_SAT",
            O2_SATURATION: "OXYGEN_SAT",
            SATURATION_O2: "OXYGEN_SAT",
            RESPIRATORY_RATE: "RESP_RATE",
            RESPIRATION_RATE: "RESP_RATE",
            BREATHING_RATE: "RESP_RATE",
            BLOOD_GLUCOSE: "GLYCEMIA",
            BLOOD_SUGAR: "GLYCEMIA",
            GLUCOSE: "GLYCEMIA",
            GLYCEMIE: "GLYCEMIA",
          }[rawType] || rawType));
      const location = bloodPressureLabel?.location || "";
      const isBloodPressure = type === "BP_SYS" || type === "BP_DIA";
      if (
        !measurementFields.some((field) => field.type === type) &&
        !isBloodPressure
      ) {
        return;
      }

      const key = isBloodPressure && location ? `${type}:${location}` : type;
      const existing = latestByType.get(key);
      if (!existing || measurementDate(measurement) > measurementDate(existing)) {
        latestByType.set(key, measurement);
      }
    });
    return latestByType;
  }, [vitalMeasurements]);
  const customMeasurements = useMemo(
    () =>
      vitalMeasurements.filter(
        (measurement) =>
          normalizeMeasurementType(measurement?.type) === "OTHER" &&
          !isDiuresisMeasurement(measurement) &&
          !parseBloodPressureLabel(measurement),
      ),
    [vitalMeasurements],
  );
  const [examValues, setExamValues] = useState({
    glasgowScore: "",
    pupils: "",
    muscleStrength: "",
    reflexes: "",
    sensitivity: "",
    coordinationGait: "",
    cardioPulmonary: "",
    peripheralPulses: "",
    ecg: "",
    abdominalExam: "",
    omi: "",
  });

  const weight = displayMeasurement(
    patientMeasurements?.weight ?? medicalInfo?.poidsKg ?? medicalInfo?.weight,
  );
  const height = displayMeasurement(
    patientMeasurements?.height ?? medicalInfo?.tailleCm ?? medicalInfo?.height,
  );
  const weightNumber = Number(weight);
  const heightNumber = Number(height);
  const bmi = weight && height && weightNumber > 0 && heightNumber > 0
    ? (weightNumber / ((heightNumber / 100) ** 2)).toFixed(1)
    : "";
  const bmiCategory = bmi
    ? Number(bmi) < 18.5
      ? "underweight"
      : Number(bmi) < 25
        ? "normal"
        : Number(bmi) < 30
          ? "overweight"
          : "obese"
    : "";
  const bloodPressureReadings = [
    ...bloodPressureLocations.map((location) => ({
      ...location,
      systolic: latestMeasurements.get(`BP_SYS:${location.key}`),
      diastolic: latestMeasurements.get(`BP_DIA:${location.key}`),
    })),
    ...(latestMeasurements.has("BP_SYS") || latestMeasurements.has("BP_DIA")
      ? [{
          key: "UNSPECIFIED",
          labelKey: "unspecifiedLocation",
          systolic: latestMeasurements.get("BP_SYS"),
          diastolic: latestMeasurements.get("BP_DIA"),
        }]
      : []),
  ];
  const uncontrolledReading = bloodPressureReadings.find(({ systolic, diastolic }) => {
    const systolicValue = Number(measurementValue(systolic));
    const diastolicValue = Number(measurementValue(diastolic));
    return systolicValue >= 140 || diastolicValue >= 90;
  });
  const pendingPressureLocations = bloodPressureLocations.filter(({ key }) => {
    const reading = bloodPressureReadings.find((item) => item.key === key);
    return (
      pressureDraft[key] !== undefined &&
      pressureDraft[key] !== bloodPressureValue(reading?.systolic, reading?.diastolic)
    );
  });

  useEffect(() => {
    const cleanUserId = String(userId || "").trim();
    if (!cleanUserId) {
      setVitalMeasurements([]);
      setPressureDraft({});
      setMeasurementsError("");
      return undefined;
    }

    let isCurrentPatient = true;
    setVitalMeasurements([]);
    setPressureDraft({});
    setPressureError("");
    setMeasurementsLoading(true);
    setMeasurementsError("");
    dispatch(fetchVitalSignals({ userId: cleanUserId }))
      .unwrap()
      .then((result) => {
        if (!isCurrentPatient) return;
        const signals = Array.isArray(result?.data) ? result.data : [];
        setVitalMeasurements(signals);
      })
      .catch((error) => {
        if (!isCurrentPatient) return;
        setMeasurementsError(
          error?.message ||
            (typeof error === "string" ? error : "") ||
            t("vitals.measurementsLoadFailed", {
              defaultValue: "Impossible de charger les mesures personnalisées.",
            }),
        );
      })
      .finally(() => {
        if (isCurrentPatient) setMeasurementsLoading(false);
      });

    return () => {
      isCurrentPatient = false;
    };
  }, [dispatch, userId, t]);

  useEffect(() => {
    setExamValues({
      glasgowScore: examRecord?.glasgowScore ?? "",
      pupils: examRecord?.pupils || "",
      muscleStrength: examRecord?.muscleStrength || "",
      reflexes: examRecord?.reflexes || "",
      sensitivity: examRecord?.sensitivity || "",
      coordinationGait: examRecord?.coordinationGait || "",
      cardioPulmonary: examRecord?.cardioPulmonary || "",
      peripheralPulses: examRecord?.peripheralPulses || "",
      ecg: examRecord?.ecg || "",
      abdominalExam: examRecord?.abdominalExam || "",
      omi: examRecord?.omi || "",
    });
  }, [
    examRecord?.id,
    examRecord?.glasgowScore,
    examRecord?.pupils,
    examRecord?.muscleStrength,
    examRecord?.reflexes,
    examRecord?.sensitivity,
    examRecord?.coordinationGait,
    examRecord?.cardioPulmonary,
    examRecord?.peripheralPulses,
    examRecord?.ecg,
    examRecord?.abdominalExam,
    examRecord?.omi,
  ]);

  const updateExamValue = (key, value) => {
    setExamValues((current) => ({ ...current, [key]: value }));
  };

  const handleAddMetric = async (metric) => {
    const cleanUserId = String(userId || "").trim();
    const value = Number(String(metric.value).trim().replace(",", "."));
    if (!cleanUserId) {
      setMetricError(t("vitals.metricMissingPatient", {
        defaultValue: "Patient non identifié : impossible d’enregistrer la mesure.",
      }));
      return;
    }
    if (!Number.isFinite(value)) {
      setMetricError(t("vitals.metricInvalidValue", {
        defaultValue: "La valeur doit être numérique pour apparaître dans l’aperçu des mesures.",
      }));
      return;
    }

    setSavingMetric(true);
    setMetricError("");
    try {
      const result = await dispatch(addVitalSignal({
        userId: cleanUserId,
        values: {
          type: "OTHER",
          customLabel: metric.label,
          value,
          unit: metric.unit || undefined,
          measuredAt: new Date().toISOString(),
        },
      })).unwrap();
      const savedMeasurement = result?.data;
      if (savedMeasurement) {
        setVitalMeasurements((current) => [
          savedMeasurement,
          ...current.filter((item) => item?.id !== savedMeasurement?.id),
        ]);
      } else {
        const refreshed = await dispatch(fetchVitalSignals({ userId: cleanUserId })).unwrap();
        const signals = Array.isArray(refreshed?.data) ? refreshed.data : [];
        setVitalMeasurements(signals);
      }
      window.dispatchEvent(new CustomEvent("patient-medical-measurements-updated", {
        detail: { userId: cleanUserId, at: Date.now() },
      }));
    } catch (error) {
      const apiMessage =
        error?.message?.message ||
        error?.message?.error ||
        error?.message;
      setMetricError(
        (typeof apiMessage === "string" ? apiMessage : "") ||
          (typeof error === "string" ? error : "") ||
          t("vitals.metricSaveFailed", {
            defaultValue: "Impossible d’enregistrer cette mesure.",
          }),
      );
    } finally {
      setSavingMetric(false);
    }
  };

  const saveBloodPressure = async (location, draftValue) => {
    const cleanUserId = String(userId || "").trim();
    if (!cleanUserId) {
      setPressureError(t("vitals.metricMissingPatient"));
      return;
    }
    const match = String(draftValue || "").match(/^\s*(\d{2,3})\s*\/\s*(\d{2,3})\s*$/);
    if (!match) {
      setPressureError(t("vitals.bloodPressureInvalid", {
        defaultValue: "Enter systolic and diastolic values as 120/80.",
      }));
      return;
    }

    setPressureSaving(location);
    setPressureError("");
    const measuredAt = new Date().toISOString();
    try {
      const saved = await Promise.all([
        dispatch(addVitalSignal({
          userId: cleanUserId,
          values: {
            type: "OTHER",
            customLabel: `BP_${location}_SYS`,
            value: Number(match[1]),
            unit: "mmHg",
            measuredAt,
          },
        })).unwrap(),
        dispatch(addVitalSignal({
          userId: cleanUserId,
          values: {
            type: "OTHER",
            customLabel: `BP_${location}_DIA`,
            value: Number(match[2]),
            unit: "mmHg",
            measuredAt,
          },
        })).unwrap(),
      ]);
      const newMeasurements = saved
        .map((result) => result?.data)
        .filter(Boolean);
      if (newMeasurements.length) {
        setVitalMeasurements((current) => [...newMeasurements, ...current]);
      } else {
        const refreshed = await dispatch(fetchVitalSignals({ userId: cleanUserId })).unwrap();
        setVitalMeasurements(Array.isArray(refreshed?.data) ? refreshed.data : []);
      }
      setPressureDraft((current) => {
        const next = { ...current };
        delete next[location];
        return next;
      });
      window.dispatchEvent(new CustomEvent("patient-medical-measurements-updated", {
        detail: { userId: cleanUserId, at: Date.now() },
      }));
    } catch (error) {
      setPressureError(
        error?.message?.message ||
          error?.message ||
          t("vitals.bloodPressureSaveFailed", {
            defaultValue: "Unable to save this blood pressure reading.",
          }),
      );
      try {
        const refreshed = await dispatch(fetchVitalSignals({ userId: cleanUserId })).unwrap();
        setVitalMeasurements(Array.isArray(refreshed?.data) ? refreshed.data : []);
      } catch (refreshError) {
        setMeasurementsError(
          refreshError?.message ||
            t("vitals.measurementsLoadFailed", {
              defaultValue: "Unable to load custom measurements.",
            }),
        );
      }
    } finally {
      setPressureSaving("");
    }
  };

  const savePendingBloodPressures = async () => {
    for (const { key } of pendingPressureLocations) {
      await saveBloodPressure(key, pressureDraft[key]);
    }
  };

  return (
    <section className="neurologue-section-card vitals-exam">
      <div className="vitals-exam__measurements vitals-exam__measurements--body">
        <label className="vitals-exam__metric">
          <span>{t("vitals.weight")}</span>
          <span className="vitals-exam__input-unit">
            <input type="text" value={weight} readOnly aria-label={t("vitals.weightAria")} />
            <i>{t("vitals.unitKg")}</i>
          </span>
        </label>
        <label className="vitals-exam__metric">
          <span>{t("vitals.height")}</span>
          <span className="vitals-exam__input-unit">
            <input type="text" value={height} readOnly aria-label={t("vitals.heightAria")} />
            <i>{t("vitals.unitCm")}</i>
          </span>
        </label>
        <div className={`vitals-exam__metric vitals-exam__metric--bmi${bmiCategory === "obese" ? " is-obese" : ""}`}>
          <span>{t("vitals.bmiAuto")}</span>
          <span className="vitals-exam__input-unit">
            <input
              type="text"
              value={bmi || "—"}
              readOnly
              aria-label={t("vitals.bmiAuto")}
            />
            <i>{bmi ? "kg/m²" : "—"}</i>
          </span>
          {bmiCategory && (
            <small className="vitals-exam__metric-note">
              {t(`vitals.bmiCategories.${bmiCategory}`)}
            </small>
          )}
        </div>
      </div>

      <div className="vitals-exam__measurements vitals-exam__measurements--clinical">
        <div className="vitals-exam__pressure-heading">
          <h3>{t("vitals.bloodPressure")}</h3>
          <button
            type="button"
            className="vitals-exam__add-metric"
            onClick={() => {
              setMetricError("");
              setAddMetricOpen(true);
            }}
            disabled={savingMetric}
            aria-label={t("vitals.addMeasurement", { defaultValue: "Ajouter une mesure" })}
            title={t("vitals.addMeasurement", { defaultValue: "Ajouter une mesure" })}
          >
            <Plus size={17} />
          </button>
          {pendingPressureLocations.length > 0 && (
            <button
              type="button"
              className="vitals-exam__pressure-save"
              onClick={savePendingBloodPressures}
              disabled={Boolean(pressureSaving)}
            >
              {pressureSaving
                ? t("vitals.saving", { defaultValue: "Saving…" })
                : t("vitals.save", { defaultValue: "Save" })}
            </button>
          )}
        </div>
        {bloodPressureReadings.map(({ key, labelKey, systolic, diastolic }) => {
          const recordedValue = bloodPressureValue(systolic, diastolic);
          const value = pressureDraft[key] ?? recordedValue;
          const unit =
            systolic?.unit ||
            diastolic?.unit ||
            systolic?.unitLabel ||
            diastolic?.unitLabel ||
            t("vitals.unitMmHg");
          return (
            <label className="vitals-exam__metric" key={key}>
              <span>{t(`vitals.${labelKey}`)}</span>
              <span className="vitals-exam__input-unit">
                <input
                  id={`vitals-bp-${key}`}
                  type="text"
                  value={value}
                  placeholder="—/—"
                  onChange={(event) => {
                    setPressureError("");
                    const numericValue = event.target.value.replace(/[^\d/]/g, "");
                    setPressureDraft((current) => ({
                      ...current,
                      [key]: numericValue,
                    }));
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      key !== "UNSPECIFIED" &&
                      pressureDraft[key] !== undefined
                    ) {
                      event.preventDefault();
                      saveBloodPressure(key, event.currentTarget.value);
                    }
                  }}
                  readOnly={key === "UNSPECIFIED"}
                  aria-label={`${t("vitals.bloodPressure")} — ${t(`vitals.${labelKey}`)}`}
                />
                <i>{unit}</i>
              </span>
            </label>
          );
        })}
        {uncontrolledReading && (
          <p className="vitals-exam__bp-alert" role="status">
            <span aria-hidden="true">⚠</span>
            {t("vitals.uncontrolledBloodPressure", {
              location: t(`vitals.${uncontrolledReading.labelKey}`),
              value: `${displayMeasurement(measurementValue(uncontrolledReading.systolic)) || "—"}/${displayMeasurement(measurementValue(uncontrolledReading.diastolic)) || "—"} ${t("vitals.unitMmHg")}`,
            })}
          </p>
        )}
        {pressureError && (
          <p className="vitals-exam__measurements-error" role="alert">
            {typeof pressureError === "string" ? pressureError : t("vitals.bloodPressureSaveFailed")}
          </p>
        )}

        {measurementFields.map((field) => {
          const measurement = latestMeasurements.get(field.type);
          const value = displayMeasurement(measurementValue(measurement));
          const unit =
            measurement?.unit ||
            measurement?.unitLabel ||
            measurement?.unitOfMeasure ||
            t(`vitals.${field.unitKey}`);
          return (
            <label className="vitals-exam__metric" key={field.key}>
              <span>{t(`vitals.${field.key}`)}</span>
              <span className="vitals-exam__input-unit">
                <input
                  type="text"
                  value={value || "—"}
                  readOnly
                  aria-label={t(`vitals.${field.key}`)}
                />
                <i>{unit}</i>
              </span>
            </label>
          );
        })}
        {customMeasurements.map((measurement, index) => (
          <label
            className="vitals-exam__metric"
            key={measurement.id ?? `${measurement.customLabel}-${measurement.measuredAt}-${index}`}
          >
            <span>
              {measurement.customLabel || measurement.label || t("vitals.customMeasurement")}
            </span>
            <span className="vitals-exam__input-unit">
              <input
                type="text"
                value={displayMeasurement(measurement.value ?? measurement.reading) || "—"}
                readOnly
                aria-label={measurement.customLabel || measurement.label || t("vitals.customMeasurement")}
              />
              {measurement.unit && <i>{measurement.unit}</i>}
            </span>
          </label>
        ))}
        {measurementsLoading && (
          <p className="vitals-exam__measurements-status" role="status">
            {t("vitals.measurementsLoading", { defaultValue: "Chargement des mesures…" })}
          </p>
        )}
        {measurementsError && (
          <p className="vitals-exam__measurements-error" role="alert">
            {measurementsError}
          </p>
        )}
      </div>

      <div className="vitals-exam__group vitals-exam__clinical">
        <div className="vitals-exam__clinical-heading">
          <h3>{t("vitals.neurologicalExam")}</h3>
        </div>
        <div className="vitals-exam__exam-grid">
          {examinationFields.map((field) => (
            <label className="vitals-exam__exam-field" key={field.key}>
              <span>{t(`vitals.${field.key}`)}</span>
              <input
                data-exam-field={field.key}
                type={field.type || "text"}
                min={field.min}
                max={field.max}
                value={examValues[field.key]}
                onChange={(event) => updateExamValue(field.key, event.target.value)}
                placeholder={field.placeholder ? t(`vitals.${field.placeholder}`) : undefined}
                aria-label={t(`vitals.${field.key}`)}
              />
            </label>
          ))}
        </div>
        <div className="vitals-exam__clinical-subsection">
          <h4>{t("vitals.clinicalExam")}</h4>
          <div className="vitals-exam__exam-grid">
            <label className="vitals-exam__exam-field">
              <span>{t("vitals.auscultation")}</span>
              <input
                data-exam-field="cardioPulmonary"
                type="text"
                value={examValues.cardioPulmonary}
                onChange={(event) => updateExamValue("cardioPulmonary", event.target.value)}
                placeholder={t("vitals.auscultationPlaceholder")}
                aria-label={t("vitals.auscultation")}
              />
            </label>
            <label className="vitals-exam__exam-field">
              <span>{t("vitals.peripheralPulses")}</span>
              <select
                data-exam-field="peripheralPulses"
                value={examValues.peripheralPulses}
                onChange={(event) => updateExamValue("peripheralPulses", event.target.value)}
                aria-label={t("vitals.peripheralPulses")}
              >
                <option value="">—</option>
                <option value="present bilat">{t("vitals.presentBilateral")}</option>
                <option value="diminished">{t("vitals.reducedBilateral")}</option>
                <option value="absent R">{t("vitals.absentRight")}</option>
                <option value="absent L">{t("vitals.absentLeft")}</option>
              </select>
            </label>
            <label className="vitals-exam__exam-field">
              <span>{t("vitals.ecg")}</span>
              <input
                data-exam-field="ecg"
                type="text"
                value={examValues.ecg}
                onChange={(event) => updateExamValue("ecg", event.target.value)}
                placeholder={t("vitals.ecgPlaceholder")}
                aria-label={t("vitals.ecg")}
              />
            </label>
            <label className="vitals-exam__exam-field">
              <span>{t("vitals.abdominal")}</span>
              <input
                data-exam-field="abdominalExam"
                type="text"
                value={examValues.abdominalExam}
                onChange={(event) => updateExamValue("abdominalExam", event.target.value)}
                placeholder={t("vitals.abdominalPlaceholder")}
                aria-label={t("vitals.abdominal")}
              />
            </label>
          </div>
          <div className="vitals-exam__edema">
            <h4>{t("vitals.edema")}</h4>
            <div className="vitals-exam__edema-options" role="group" aria-label={t("vitals.edemaAria")}>
              {edemaOptions.map((option) => (
                <button
                  type="button"
                  className={`vitals-exam__edema-option is-${option}${examValues.omi === option ? " is-active" : ""}`}
                  data-exam-field="omi"
                  data-value={option}
                  aria-pressed={examValues.omi === option}
                  key={option}
                  onClick={() => updateExamValue("omi", option)}
                >
                  {t(`vitals.${option}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {metricError && <p className="vitals-exam__metric-error" role="alert">{metricError}</p>}
      <AddMetricModal
        open={addMetricOpen}
        onClose={() => setAddMetricOpen(false)}
        onAdd={handleAddMetric}
        simple
      />
      {examLoading && <p role="status">{t("common.loading", { defaultValue: "Chargement de l’examen…" })}</p>}
      {examError && <p role="alert">{String(examError)}</p>}
    </section>
  );
}
